import React from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from 'CommonComponents/Button';
import SelectFromField from 'CommonComponents/SelectFromField';

const AddTimeRowScheme = Yup.object().shape({
    event_id: Yup.string().required('This value is required'),
    role_id: Yup.string().required('This value is required'),
    type: Yup.string().oneOf(['internal', 'freelance']).required('This value is required'),
});

function roleLabel(role) {
    return `${role.name} · ${role.category}`;
}

/**
 * Adds an empty row for a project and role. It writes nothing on its own — the
 * row appears with no days recorded and the first cell the user fills is what
 * creates an entry.
 *
 * The picker offers only projects the user is a member of and only roles active
 * on the rate card in force during the week, both decided server-side. No rate
 * reaches this endpoint, cost or billed. A submitted week takes no new rows.
 */
export default function AddTimeRowForm({ projects, roles, existingKeys, readOnly = false, onAdd, onDuplicate }) {
    const disabled = readOnly || projects.length === 0 || roles.length === 0;

    return (
        <Formik
            initialValues={{ event_id: '', role_id: '', type: 'internal' }}
            validationSchema={AddTimeRowScheme}
            onSubmit={(values, actions) => {
                const role = roles.find((candidate) => String(candidate.id) === String(values.role_id));

                if (!role) {
                    actions.setSubmitting(false);

                    return;
                }

                const key = `${values.event_id}|${role.name}|${role.category}`;

                // A project and role already on the week is one row, not two:
                // both would carry the same identity and the server would
                // upsert them onto each other.
                if (existingKeys.includes(key)) {
                    onDuplicate(key);
                    actions.setSubmitting(false);

                    return;
                }

                onAdd({
                    event_id: Number(values.event_id),
                    role,
                    type: values.type,
                });

                actions.resetForm();
                actions.setSubmitting(false);
            }}
        >
            {({ isSubmitting, handleSubmit, setFieldValue }) => (
                <div className="table__card">
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-mb-6">
                        <h2 className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-0">{$t('Add a row')}</h2>
                        <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                            {$t('One row per project and role. The same project can appear on several rows.')}
                        </p>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-4 md:tw-items-end">
                        <SelectFromField label={$t('Project')} as="select" name="event_id" disabled={disabled}>
                            <option value="">{$t('Select a project')}</option>
                            {projects.map((project) => (
                                <option key={project.id} value={project.id}>
                                    {project.name}
                                </option>
                            ))}
                        </SelectFromField>

                        <SelectFromField
                            label={$t('Role')}
                            as="select"
                            name="role_id"
                            disabled={disabled}
                            onChange={(event) => {
                                const role = roles.find((candidate) => String(candidate.id) === String(event.target.value));

                                setFieldValue('role_id', event.target.value);

                                // The role's own default is the starting point;
                                // the row's type stays editable afterwards.
                                if (role) {
                                    setFieldValue('type', role.default_type);
                                }
                            }}
                        >
                            <option value="">{$t('Select a role')}</option>
                            {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                    {roleLabel(role)}
                                </option>
                            ))}
                        </SelectFromField>

                        <SelectFromField label={$t('Type')} as="select" name="type" disabled={disabled}>
                            <option value="internal">{$t('Internal')}</option>
                            <option value="freelance">{$t('Freelance')}</option>
                        </SelectFromField>

                        <div className="tw-flex tw-items-center tw-justify-end">
                            <Button variant="primary" disabled={disabled || !!isSubmitting} onClick={handleSubmit}>
                                {$t('Add row')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Formik>
    );
}
